// Route-scopes PostHog session replay. This is the TRUST-CRITICAL control for the engagement
// slice: replay is DEFAULT-DENY and records ONLY on the low-sensitivity routes below (no ingested
// content on screen). Every other hash — including new/unknown routes — stops recording, so a
// content surface added later is safe by default. Route-scoping is the control; input/text
// masking (configured in analytics.ts) is only a backstop, never relied on to make a route safe.
//
// HashRouter note: react-router's useLocation().pathname is the IN-HASH path (e.g. "/sources"),
// which is exactly what we want — NOT window.location.pathname (always "/").

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { startSessionReplay, stopSessionReplay } from '../lib/analytics';

// Allowlist (spec: 2026-07-14-posthog-engagement-slice.plan.md § "Session replay — route scope").
// Deliberately EXCLUDES every content route: /briefing, /briefing-history*, /standup, /live*,
// /map, /people, /conversations/*, /my-conversations/*, /team-conversations/*.
const REPLAY_ALLOWED_EXACT = new Set<string>(['/sources', '/settings', '/privacy']);
const REPLAY_ALLOWED_PREFIXES = ['/invite/']; // onboarding entry (may show a team name — low)

export function isReplayAllowedRoute(path: string): boolean {
  if (REPLAY_ALLOWED_EXACT.has(path)) return true;
  return REPLAY_ALLOWED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

// `authenticated` covers the pre-auth login screen: when signed out, AppLayout renders only the
// AuthScreen / invite / loader — no content surface is mounted — so recording the login funnel is
// safe regardless of which hash the URL happens to carry.
// The record decision, extracted as a pure function so the trust-critical rule is unit-testable
// (this SPA has no testing-library to render the hook). Record when: signed out (only the
// content-free AuthScreen is mounted), OR forceRecord is set, OR the route is on the allowlist.
//
// `forceRecord` unconditionally enables recording regardless of route. Its one caller is
// first-run onboarding (OnboardingGate): the wizard is a full-screen takeover rendered OUTSIDE
// the router, so it has no allowlisted hash of its own — but it's a brand-new user with ZERO
// ingested content on screen (setup steps only), which makes it the highest-value and
// lowest-risk replay we have. Record it explicitly rather than by faking a route.
export function shouldRecordReplay(authenticated: boolean, forceRecord: boolean, path: string): boolean {
  return !authenticated || forceRecord || isReplayAllowedRoute(path);
}

export function useSessionReplayScope(authenticated: boolean, forceRecord = false): void {
  const location = useLocation();
  useEffect(() => {
    if (shouldRecordReplay(authenticated, forceRecord, location.pathname)) startSessionReplay();
    else stopSessionReplay();
  }, [location.pathname, authenticated, forceRecord]);
}
